import { useNavigate } from "@tanstack/react-router";

import { Skeleton } from "@/components/ui/skeleton";
import { MarkdownView } from "@/lib/markdown";

import { usePanelData, usePreviewQuery, type ReportPanel } from "../api";
import { applyColumnFilters, resolveDrilldown, type ColumnFilter } from "../viz";
import { PanelChart } from "./PanelChart";
import { StatView } from "./StatView";

// TanStack's typed navigate rejects runtime-arbitrary routes; drill-down targets
// are validated (in-app only) in resolveDrilldown, so we call a loosened form.
type LooseNavigate = (opts: { to: string; search?: Record<string, string> }) => void;

/**
 * Renders a panel's *content* (no card chrome): runs its stored read-only query
 * (cached) and draws the chart/grid, or shows a text panel's prose. Shared by
 * the builder's saved-panel strip ({@link PanelCard}) and the dashboard grid so
 * the query-run + drill-down behavior lives in one place. `className` sizes the
 * chart (e.g. `h-52` or `h-full`).
 */
export function PanelView({
  panel,
  className,
  params,
  columnFilters,
  reportId,
  cached = false,
}: {
  panel: ReportPanel;
  className?: string;
  /** Dashboard filter values — fill the query's {{name}} placeholders. */
  params?: Record<string, string>;
  /** Dashboard column auto-filters — narrow the result rows client-side. */
  columnFilters?: ColumnFilter[];
  /** The report this panel belongs to — required for cached reads. */
  reportId?: string;
  /** Viewer: read server-cached rows (the panel-data endpoint) instead of
   *  running the query live. The builder leaves this off so authoring is live. */
  cached?: boolean;
}) {
  const navigate = useNavigate() as unknown as LooseNavigate;
  const query = panel.query;
  const useCached = cached && !!reportId;
  const live = usePreviewQuery(query?.source, query?.sql, params, !!query?.sql && !useCached);
  const cachedQ = usePanelData(reportId, panel.id, params, !!query?.sql && useCached);
  const preview = useCached ? cachedQ : live;

  if (panel.type === "text") {
    return <MarkdownView source={panel.viz.content ?? panel.description ?? ""} className="p-1" />;
  }

  const drilldown = panel.viz.drilldown;
  const onDatumClick = drilldown
    ? (datum: Record<string, unknown>) => {
        const target = resolveDrilldown(drilldown, datum);
        if (target) navigate({ to: target.to, search: target.search });
      }
    : undefined;

  if (!query?.sql) {
    return (
      <p className="py-6 text-center text-xs text-muted-foreground">
        This panel has no saved query.
      </p>
    );
  }
  if (preview.isLoading) return <Skeleton className={className ?? "h-52 w-full"} />;
  if (preview.isError) {
    return (
      <p className="py-6 text-center text-xs text-danger">
        {(preview.error as Error)?.message ?? "Couldn’t run this panel’s query."}
      </p>
    );
  }
  if (!preview.data) return null;
  const result = applyColumnFilters(
    { columns: preview.data.columns, rows: preview.data.rows },
    columnFilters,
  );
  if (panel.type === "stat") {
    return <StatView result={result} viz={panel.viz} className={className} />;
  }
  return (
    <PanelChart
      result={result}
      viz={panel.viz}
      mode={panel.type === "grid" ? "table" : "chart"}
      className={className}
      onDatumClick={onDatumClick}
    />
  );
}
